import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, ChevronDown, ChevronUp, Search } from 'lucide-react';
import { Market, MarketSearch } from '../../types/market';
import { useChatInput } from '../../contexts/ChatContext';
import { useUserLikes } from '../../hooks/useUserLikes';
import { useUserHistory } from '../../hooks/useUserHistory';
import { useAuth } from '../auth/AuthContext';
import { SignInPrompt } from '../auth/SignInPrompt';

interface MarketCardProps {
  market: Market;
  searches: MarketSearch[];
  isExpanded: boolean;
  onToggle: () => void;
}

export const MarketCard: React.FC<MarketCardProps> = ({ 
  market, 
  searches,
  isExpanded,
  onToggle
}) => {
  const [showSignInPrompt, setShowSignInPrompt] = useState(false);
  const { setInputValue } = useChatInput();
  const { isLiked, toggleLike } = useUserLikes();
  const { recordSearch } = useUserHistory();
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleSearchClick = async (e: React.MouseEvent, searchId: string, query: string) => {
    e.stopPropagation();
    setInputValue(query);
    if (user) {
      await recordSearch(query);
    }
    navigate(`/markets/${market.id}`, { 
      state: { 
        query,
        searchMetadata: {
          searchId,
          searchCategory: 'market_search'
        }
      } 
    });
    
    // Scroll to chat section on mobile
    setTimeout(() => {
      const chatSection = document.querySelector('[data-chat-section]');
      if (chatSection && window.innerWidth < 768) {
        chatSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 300); // Slightly longer delay to allow for navigation
  };

  const handleLikeClick = (e: React.MouseEvent, searchId: string) => {
    e.preventDefault(); // Prevent Link navigation
    e.stopPropagation(); // Stop event bubbling
    if (!user) {
      setShowSignInPrompt(true);
      return;
    }
    toggleLike(searchId);
  };

  // Sort searches by number of searches and then by likes
  const sortedSearches = [...searches].sort((a, b) => {
    if (b.search.searches !== a.search.searches) {
      return b.search.searches - a.search.searches;
    }
    return b.search.likes - a.search.likes;
  });

  return (
    <>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md transition-colors h-full">
        <button
          onClick={onToggle}
          className="w-full text-left p-6 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset rounded-lg"
        >
          <div className="flex justify-between items-start">
            <div className="min-h-[7rem] flex flex-col">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                {market.name}
              </h2>
              <div className="flex-grow">
                {market.description && (
                  <p className="text-gray-600 dark:text-gray-300 text-sm line-clamp-2 min-h-[2.5rem]">
                    {market.description}
                  </p>
                )}
              </div>
              <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {searches.length} {searches.length === 1 ? 'search' : 'searches'}
              </div>
            </div>
            <div className="ml-4 text-gray-400 dark:text-gray-500">
              {isExpanded ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </div>
          </div>
        </button>

        {isExpanded && (
          <div className="px-6 pb-6 border-t border-gray-100 dark:border-gray-700">
            <div className="pt-4">
              {searches.length === 0 ? (
                <div className="text-center py-8 px-4">
                  <Search className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-500 mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    No searches found
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    This market doesn't have any searches yet.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {sortedSearches.map((item) => (
                    <div
                      key={item.id}
                      className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
                    >
                      <div className="flex items-center justify-between">
                        <button
                          onClick={(e) => handleSearchClick(e, item.search.id, item.search.query)}
                          className="flex-1 text-left"
                        >
                          <p className="text-sm text-gray-700 dark:text-gray-200 group-hover:text-gray-900 dark:group-hover:text-white line-clamp-2">
                            {item.search.query}
                          </p>
                          <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 dark:text-gray-400">
                            <span>{item.search.searches.toLocaleString()} searches</span>
                            <span className="text-gray-300 dark:text-gray-600">•</span>
                            <span className="flex items-center gap-1">
                              <Heart className="w-3 h-3" />
                              {item.search.likes}
                            </span>
                          </div>
                        </button>
                        <button
                          onClick={(e) => handleLikeClick(e, item.search.id)}
                          className={`p-1.5 rounded-full transition-colors ${
                            isLiked(item.search.id)
                              ? 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20'
                              : 'text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-600 dark:hover:text-gray-300'
                          }`}
                        >
                          <Heart 
                            className="w-4 h-4" 
                            fill={isLiked(item.search.id) ? 'currentColor' : 'none'} 
                          />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {showSignInPrompt && (
        <SignInPrompt onClose={() => setShowSignInPrompt(false)} />
      )}
    </>
  );
};